/**
 * EZ-IOK 간편인증 서비스
 * 드림시큐리티 EZ-IOK 표준창/통합창 연동
 *
 * @see https://ez-iok.com/guide/eziok_std_guide/
 */

// 백엔드 API URL (환경변수에서 가져오거나 기본값 사용)
const API_BASE = process.env.REACT_APP_API_BASE_URL || 'https://api.liposet.co.kr';

// EZ-IOK 콜백 URL (EZ-IOK 서버에 등록된 URL과 일치해야 함)
const EZIOK_CALLBACK_URL = `${API_BASE}/func/eziok/callback`;

declare global {
  interface Window {
    // 표준창: SDK가 인증사업자 선택 UI 제공
    eziok_std_process?: (
      requestUrl: string,
      browserType: string,
      callbackFuncName: string,
    ) => void;
    // 통합창: 인증사업자 미리 지정, 선택 UI 스킵
    eziok_std_process1?: (
      requestUrl: string,
      browserType: string,
      callbackFuncName: string,
    ) => void;
  }
}

// 인증사업자 정보 (백엔드 API 응답 형식에 맞춤)
export interface EZIOKProvider {
  code: string;        // 인증사업자 코드 (kakao, naver 등)
  name: string;        // 인증사업자명
  type: string;        // 타입 (민간, 통신사, 금융, 공동인증)
  ciStorable: boolean; // CI 저장 가능 여부 (false면 ID/PW찾기 용도만)
}

// 인증 요청 파라미터
export interface EZIOKRequestParams {
  userId?: number;           // 사용자 ID (로그인 상태면 전달)
  authProviderCode: string;  // 인증사업자 코드 (필수)
  resultUrl?: string;        // 결과 수신 URL
  serviceType?: 'auth' | 'auth_ext' | 'auth_adult' | 'sign';
  retType?: 'callback' | 'redirect';
  retTransferType?: 'keytoken' | 'hubtoken';
}

// 인증 결과 (postMessage로 수신)
export interface EZIOKResult {
  success: boolean;
  message: string;
  data: {
    name?: string;
    ci?: string;
    di?: string;
    birthDate?: string;
    gender?: string;
    nationalInfo?: string;
    mobileNo?: string;
    mobileCo?: string;
  };
}

// SDK 로드 상태
type SDKStatus = 'idle' | 'loading' | 'loaded' | 'error';

// 인증 환경 정보
export interface AuthEnvironment {
  browserType: 'WB' | 'MB';
  retType: 'callback' | 'redirect';
  isInAppBrowser: boolean;
  inAppBrowserType: string | null;
  isIOSSafari: boolean;
  isFlutterApp: boolean;
}

class EZIOKService {
  private sdkStatus: SDKStatus = 'idle';
  private sdkLoadPromise: Promise<void> | null = null;

  // 개발/운영 환경 구분
  private readonly isDev =
    process.env.NODE_ENV === 'development' ||
    window.location.hostname === 'localhost';

  // SDK URL (개발: scert, 운영: cert)
  private readonly SDK_URL = this.isDev
    ? 'https://scert.ez-iok.com/stdauth/ds_auth_ptb/asset/js/ptb_ezauth_proc.js'
    : 'https://cert.ez-iok.com/stdauth/ds_auth_ptb/asset/js/ptb_ezauth_proc.js';

  /**
   * EZ-IOK SDK 스크립트 로드
   */
  loadScript(): Promise<void> {
    if (this.sdkStatus === 'loaded') {
      return Promise.resolve();
    }

    if (this.sdkLoadPromise) {
      return this.sdkLoadPromise;
    }

    this.sdkStatus = 'loading';
    this.sdkLoadPromise = new Promise((resolve, reject) => {
      // 이미 로드된 경우
      if (window.eziok_std_process || window.eziok_std_process1) {
        this.sdkStatus = 'loaded';
        resolve();
        return;
      }

      const script = document.createElement('script');
      script.type = 'text/javascript';
      script.src = this.SDK_URL;
      script.async = true;

      script.onload = () => {
        this.sdkStatus = 'loaded';
        console.log('[EZIOK] SDK loaded successfully');
        resolve();
      };

      script.onerror = () => {
        this.sdkStatus = 'error';
        this.sdkLoadPromise = null;
        const error = new Error('[EZIOK] Failed to load SDK');
        console.error(error);
        reject(error);
      };

      document.head.appendChild(script);
    });

    return this.sdkLoadPromise;
  }

  /**
   * 통합창 실행 (인증사업자 미리 지정, 선택 UI 스킵)
   * @param requestUrl 백엔드 요청 URL (authProviderCode 포함)
   * @param browserType 브라우저 타입 (WB=PC웹, MB=모바일웹, MWV=앱 WebView)
   * @param callbackFuncName 콜백 함수명 (문자열)
   */
  async processIntegrated(
    requestUrl: string,
    browserType: string = 'WB',
    callbackFuncName: string = '',
  ): Promise<void> {
    await this.loadScript();

    if (!window.eziok_std_process1) {
      throw new Error('[EZIOK] SDK (eziok_std_process1) not available');
    }

    console.log('[EZIOK] Calling eziok_std_process1 with:', {
      requestUrl,
      browserType,
      callbackFuncName,
    });

    window.eziok_std_process1(requestUrl, browserType, callbackFuncName);
  }

  /**
   * 클라이언트 txId 생성
   * 형식: EZIOK_{userId}_{timestamp}_{random}
   */
  private generateClientTxId(userId: number): string {
    const timestamp = Date.now();
    const random = Math.random().toString(36).substring(2, 10);
    return `EZIOK_${userId}_${timestamp}_${random}`;
  }

  /**
   * 간편인증 시작 (통합창 방식)
   * 프론트엔드에서 txId를 생성하여 백엔드로 전달
   *
   * 인앱 브라우저/iOS Safari에서는 redirect 방식 사용
   */
  async startAuth(params: EZIOKRequestParams): Promise<void> {
    const userId = params.userId || 0;

    // 프론트엔드에서 txId를 먼저 생성
    const clientTxId = this.generateClientTxId(userId);

    // 환경 감지 (인앱 브라우저, iOS Safari 등)
    const env = this.detectAuthEnvironment();

    console.log('[EZIOK] Auth environment:', env);

    // redirect 방식이면 복귀 경로 저장
    if (env.retType === 'redirect') {
      try {
        sessionStorage.setItem('auth_return_path', window.location.href);
        console.log('[EZIOK] Saved return path:', window.location.href);
      } catch (e) {
        console.warn('[EZIOK] Failed to save return path:', e);
      }
    }

    // callback 방식일 때만 sessionStorage에 txId 백업 저장
    if (env.retType === 'callback') {
      try {
        sessionStorage.setItem('eziok_txId', clientTxId);
        console.log('[EZIOK] Generated clientTxId:', clientTxId);
      } catch (storageError) {
        // sessionStorage 사용 불가 시 redirect 방식으로 전환
        console.warn('[EZIOK] sessionStorage unavailable, switching to redirect mode');
        env.retType = 'redirect';
      }
    }

    // localStorage에서 인증 토큰 가져오기
    const accessToken = localStorage.getItem('token');

    // resultUrl에 clientTxId와 retType 포함
    // - clientTxId: redirect 방식에서 txId 유지
    // - retType: 백엔드 콜백에서 callback/redirect 모드 구분
    const baseResultUrl = params.resultUrl || EZIOK_CALLBACK_URL;
    const resultUrl = `${baseResultUrl}${baseResultUrl.includes('?') ? '&' : '?'}clientTxId=${clientTxId}&retType=${env.retType}`;

    const queryParams = new URLSearchParams({
      userId: String(userId),
      authProviderCode: params.authProviderCode,
      serviceType: params.serviceType || 'auth',
      retType: env.retType,  // 환경에 따라 callback 또는 redirect
      retTransferType: params.retTransferType || 'keytoken',
      clientTxId: clientTxId,
      resultUrl: resultUrl,
    });

    // 인증 토큰이 있으면 쿼리 파라미터로 추가
    if (accessToken) {
      queryParams.append('access_token', accessToken);
    }

    const requestUrl = `${API_BASE}/func/eziok/request?${queryParams.toString()}`;

    console.log('[EZIOK] Starting auth with:', {
      requestUrl,
      browserType: env.browserType,
      retType: env.retType,
      clientTxId,
      isInAppBrowser: env.isInAppBrowser,
    });

    // redirect 방식이면 콜백 함수명을 빈 문자열로 전달
    const callbackFuncName = env.retType === 'callback' ? 'eziokCallback' : '';
    await this.processIntegrated(requestUrl, env.browserType, callbackFuncName);
  }

  /**
   * SDK 로드 상태 확인
   */
  isLoaded(): boolean {
    return this.sdkStatus === 'loaded' && !!(window.eziok_std_process || window.eziok_std_process1);
  }

  /**
   * 인증 환경 감지
   * - browserType: WB(PC) 또는 MB(모바일) - MWV는 푸시 알림이 안 오므로 사용하지 않음
   * - retType: callback(팝업) 또는 redirect(페이지이동)
   * - 인앱 브라우저/iOS Safari에서는 redirect 방식 사용
   */
  detectAuthEnvironment(): AuthEnvironment {
    const ua = navigator.userAgent;
    const lowerUA = ua.toLowerCase();

    // 모바일 여부 판단
    const isMobile = /android|iphone|ipad|ipod|mobile/i.test(ua);

    // 인앱 브라우저 감지
    const inAppPatterns: Record<string, RegExp> = {
      'KakaoTalk': /kakaotalk/i,
      'Naver': /naver\(/i,  // "NAVER(" 형식으로 체크
      'Instagram': /instagram/i,
      'Facebook': /fban|fbav/i,
      'Line': /line\//i,
      'Band': /band\//i,
      'Twitter': /twitter/i,
    };

    let inAppBrowserType: string | null = null;
    for (const [name, pattern] of Object.entries(inAppPatterns)) {
      if (pattern.test(ua)) {
        inAppBrowserType = name;
        break;
      }
    }

    // iOS Safari 감지 (팝업이 새 탭으로 열려서 문제 발생)
    const isIOSSafari = /iphone|ipad/i.test(ua) &&
                        /safari/i.test(ua) &&
                        !/chrome|crios|fxios/i.test(ua);

    // Flutter WebView 감지
    const isFlutterApp = localStorage.getItem('mobileApp') !== null;

    // 일반 WebView 감지 (Safari 문자열이 없는 iOS 브라우저)
    const isGenericWebView = (lowerUA.includes('iphone') || lowerUA.includes('ipad')) &&
                             !lowerUA.includes('safari');

    // redirect 사용 조건:
    // 1. 인앱 브라우저 (카카오톡, 네이버 등)
    // 2. Flutter WebView
    // 3. iOS Safari (팝업이 새 탭으로 열림)
    // 4. 일반 WebView
    const shouldUseRedirect = !!(
      inAppBrowserType ||
      isFlutterApp ||
      isIOSSafari ||
      isGenericWebView
    );

    return {
      browserType: isMobile ? 'MB' : 'WB',  // MWV 대신 MB 사용 (푸시 알림 정상 수신)
      retType: shouldUseRedirect ? 'redirect' : 'callback',
      isInAppBrowser: !!inAppBrowserType,
      inAppBrowserType,
      isIOSSafari,
      isFlutterApp,
    };
  }

  /**
   * 디바이스 타입 감지 (하위 호환용)
   * WB: PC 웹, MB: 모바일 웹
   * @deprecated detectAuthEnvironment() 사용 권장
   */
  detectBrowserType(): 'WB' | 'MB' {
    const isMobile = /android|iphone|ipad|ipod|mobile/i.test(navigator.userAgent);
    return isMobile ? 'MB' : 'WB';
  }
}

export default new EZIOKService();
